import React, { useState, useEffect, useCallback } from 'react';
import { Helmet } from 'react-helmet-async';
import { motion } from 'framer-motion';
import { Lightbulb, LightbulbOff, Zap, Lock, ArrowLeft, ShieldCheck } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { getPublicPackages } from '../services/publicApi';
import { useAuth } from '../context/AuthContext';
import BillingAddressModal from '../components/BillingAddressModal';
import PlanDetailModal from '../components/PlanDetailModal';
import apiClient from '../lib/api/client';

/* ── Tiers that should NEVER appear on the public pricing page ─── */
const HIDDEN_TIER_KEYS = ['lpu', 'lpu_faculty', 'institutional_lpu', 'lpu_premium'];

/* ── Dynamically load the Cashfree JS SDK ────────────────────────── */
const loadCashfreeSDK = () =>
    new Promise((resolve, reject) => {
        if (window.Cashfree) return resolve(window.Cashfree);
        const existing = document.getElementById('cashfree-sdk');
        if (existing) { existing.onload = () => resolve(window.Cashfree); return; }
        const script = document.createElement('script');
        script.id = 'cashfree-sdk';
        script.src = 'https://sdk.cashfree.com/js/v3/cashfree.js';
        script.async = true;
        script.onload = () => resolve(window.Cashfree);
        script.onerror = () => reject(new Error('Failed to load Cashfree SDK'));
        document.body.appendChild(script);
    });

/* ── initiateCashfreeCheckout ────────────────────────────────────── */
const initiateCashfreeCheckout = async ({ paymentSessionId, orderId, env }) => {
    await loadCashfreeSDK();
    const mode = (env || 'production').toLowerCase() === 'sandbox' ? 'sandbox' : 'production';
    // eslint-disable-next-line no-undef
    const cashfree = await Cashfree({ mode });
    const result = await cashfree.checkout({
        paymentSessionId,
        redirectTarget: '_self',
    });
    if (result.error) {
        throw new Error(result.error.message || 'Payment failed');
    }
    return result;
};

/* ═══════════════════════════════════════════════════════════════════
   PricingPage
══════════════════════════════════════════════════════════════════════ */
const PricingPage = () => {
    const navigate = useNavigate();
    const { user } = useAuth();

    const [billingCycle, setBillingCycle] = useState('monthly');
    const [packages, setPackages] = useState([]);
    const [loading, setLoading] = useState(true);

    /* modal state */
    const [selectedPlan, setSelectedPlan] = useState(null); // plan detail modal
    const [billingPlan, setBillingPlan] = useState(null);   // billing address modal
    const [payLoading, setPayLoading] = useState(false);
    const [payError, setPayError] = useState('');

    /* ── fetch plans ─────────────────────────────────────────── */
    useEffect(() => {
        const fetchTiers = async () => {
            try {
                setLoading(true);
                const res = await getPublicPackages();
                if (res.status === 'success') {
                    const visible = res.data
                        .filter(p => !HIDDEN_TIER_KEYS.includes((p.tier_key || '').toLowerCase()))
                        .sort((a, b) => (a.order || 0) - (b.order || 0));
                    setPackages(visible);
                }
            } catch (err) {
                console.error('Failed to load packages', err);
            } finally {
                setLoading(false);
            }
        };
        fetchTiers();
    }, []);

    /* ── price display helper ───────────────────────────────────── */
    const getPriceData = useCallback((pkg) => {
        const getNum = (str) => parseInt((str || '').replace(/[^\d]/g, '') || '0');
        const monthlyVal = getNum(pkg.price);
        if (billingCycle === 'yearly') {
            const displayPrice = pkg.yearly_price || `₹${(monthlyVal * 12).toLocaleString('en-IN')}`;
            const oldPriceVal = pkg.old_price || (monthlyVal > 0 ? `₹${(monthlyVal * 1.5 * 12).toLocaleString('en-IN')}` : null);
            return { price: displayPrice, period: pkg.yearly_price ? '' : '/Year', oldPrice: oldPriceVal };
        }
        return { price: pkg.price, period: pkg.period, oldPrice: pkg.old_price };
    }, [billingCycle]);

    /* ── card CTA label ─────────────────────────────────────────── */
    const getCtaLabel = (pkg) => {
        if (pkg.tier_key === 'institutional') return 'CONTACT FOR DEMO';
        return 'SELECT PLAN';
    };

    /* ── handle card click ──────────────────────────────────────── */
    const handleCardClick = (pkg) => {
        if (pkg.tier_key === 'institutional') {
            navigate('/contact');
            return;
        }
        setSelectedPlan(pkg);
    };

    /* ── handle subscribe (from PlanDetailModal) ────────────────── */
    const handleSubscribe = (pkg) => {
        if ((pkg.name || '').toLowerCase().includes('free')) {
            navigate('/register');
            return;
        }
        if (!user) {
            navigate('/login', { state: { from: '/pricing', intent: 'subscribe', planKey: pkg.tier_key } });
            return;
        }
        setSelectedPlan(null);       // close detail modal
        setBillingPlan(pkg);         // open billing modal
        setPayError('');
    };

    /* ── handle billing confirmed → create order → Cashfree ─────── */
    const handleBillingConfirmed = async (billingAddress) => {
        if (!billingPlan) return;
        setPayLoading(true);
        setPayError('');

        try {
            const payload = {
                tier_key: billingPlan.tier_key,
                billing_cycle: billingCycle,
                phone: user?.phone || '9999999999',
                billing_address: billingAddress,
            };

            const res = await apiClient.post('/api/v1/payments/create-order', payload);
            const { payment_session_id, order_id, cashfree_env } = res.data;

            if (!payment_session_id) throw new Error('No payment session received from server');

            setBillingPlan(null); // close billing modal before redirect
            await initiateCashfreeCheckout({
                paymentSessionId: payment_session_id,
                orderId: order_id,
                env: cashfree_env,
            });
        } catch (err) {
            console.error('Payment error:', err);
            const msg = err?.response?.data?.detail || err.message || 'Payment failed. Please try again.';
            setPayError(msg);
        } finally {
            setPayLoading(false);
        }
    };

    /* ── separate grid vs institutional ────────────────────────── */
    const mainPackages = packages.filter(p => p.tier_key !== 'institutional');
    const institutionalPkg = packages.find(p => p.tier_key === 'institutional');

    /* ══════════════════════════════════════════════════════════════
       Render
    ═══════════════════════════════════════════════════════════════ */
    return (
        <div style={{ minHeight: '100vh', background: '#050505', color: '#fff', fontFamily: '"Outfit", sans-serif', overflow: 'hidden', position: 'relative' }}>
            <Helmet>
                <title>Plans &amp; Pricing | TeachGenie.ai</title>
                <meta name="description" content="View TeachGenie.ai's subscription plans. Multi-tier AI solutions for educators." />
            </Helmet>

            {/* Back button */}
            <div
                onClick={() => navigate(-1)}
                style={{ position: 'absolute', top: '30px', left: '30px', cursor: 'pointer', zIndex: 10, display: 'flex', alignItems: 'center', gap: '8px', color: '#fff', opacity: 0.8, fontSize: '14px', fontWeight: '700' }}
            >
                <ArrowLeft size={20} /> <span>BACK</span>
            </div>

            {/* Background glow */}
            <div style={{ position: 'absolute', top: 0, left: '50%', transform: 'translateX(-50%)', width: '100%', height: '500px', background: 'radial-gradient(circle at center, rgba(245, 158, 11, 0.08), transparent 70%)', pointerEvents: 'none' }} />

            <div style={{ maxWidth: '1400px', margin: '0 auto', padding: '100px 24px 60px', position: 'relative', zIndex: 1 }}>

                {/* ── Header ─────────────────────────────────────── */}
                <div style={{ textAlign: 'center', marginBottom: '80px' }}>
                    <motion.h1
                        initial={{ opacity: 0, y: -20 }}
                        animate={{ opacity: 1, y: 0 }}
                        style={{ fontSize: '48px', fontWeight: '900', marginBottom: '16px', letterSpacing: '-1px', color: '#fff', textTransform: 'uppercase' }}
                    >
                        CHOOSE YOUR <span style={{ color: '#f59e0b' }}>PLAN</span>
                    </motion.h1>
                    <p style={{ fontSize: '18px', color: '#d1d5db', maxWidth: '600px', margin: '0 auto', fontWeight: '500' }}>
                        Flexible packages designed for educators of all sizes.
                    </p>

                    {/* Billing toggle */}
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', marginTop: '40px', gap: '15px' }}>
                        <div style={{ display: 'flex', background: 'rgba(255,255,255,0.05)', padding: '6px', borderRadius: '14px', border: '1px solid rgba(255,255,255,0.15)' }}>
                            <div
                                onClick={() => setBillingCycle('monthly')}
                                style={{ padding: '10px 24px', borderRadius: '10px', background: billingCycle === 'monthly' ? '#fff' : 'transparent', color: billingCycle === 'monthly' ? '#000' : '#d1d5db', cursor: 'pointer', transition: 'all 0.3s', fontSize: '14px', fontWeight: '900' }}
                            >
                                MONTHLY
                            </div>
                            <div
                                onClick={() => setBillingCycle('yearly')}
                                style={{ padding: '10px 24px', borderRadius: '10px', background: billingCycle === 'yearly' ? '#fff' : 'transparent', color: billingCycle === 'yearly' ? '#000' : '#d1d5db', cursor: 'pointer', transition: 'all 0.3s', fontSize: '14px', fontWeight: '900', display: 'flex', alignItems: 'center', gap: '8px' }}
                            >
                                YEARLY <span style={{ fontSize: '10px', background: '#f59e0b', color: '#fff', padding: '2px 6px', borderRadius: '4px' }}>SAVE 20%</span>
                            </div>
                        </div>
                    </div>
                </div>

                {/* ── Plan Cards Grid ──────────────────────────── */}
                <div style={{
                    display: 'grid',
                    gridTemplateColumns: mainPackages.length >= 4 ? 'repeat(4, 1fr)' : `repeat(${mainPackages.length || 4}, 1fr)`,
                    gap: '20px',
                    marginBottom: '40px'
                }}>
                    {loading ? (
                        Array(4).fill(0).map((_, i) => (
                            <div key={i} style={{ height: '500px', background: 'rgba(255,255,255,0.02)', borderRadius: '24px', border: '1px solid rgba(255,255,255,0.1)', animation: 'pulse 1.5s infinite' }} />
                        ))
                    ) : mainPackages.map((pkg, index) => {
                        const priceData = getPriceData(pkg);
                        const isFeatured = pkg.featured;

                        return (
                            <motion.div
                                key={pkg.id || index}
                                whileHover={{ y: -8, transition: { duration: 0.2 } }}
                                style={{
                                    background: isFeatured ? 'rgba(245,158,11,0.04)' : 'rgba(255,255,255,0.03)',
                                    border: isFeatured ? '2.5px solid #f59e0b' : '1px solid rgba(255,255,255,0.25)',
                                    borderRadius: '24px',
                                    padding: '24px',
                                    display: 'flex',
                                    flexDirection: 'column',
                                    position: 'relative',
                                    width: '100%',
                                    cursor: 'pointer',
                                }}
                                onClick={() => handleCardClick(pkg)}
                            >
                                {pkg.badge && (
                                    <div style={{ position: 'absolute', top: '-14px', right: '30px', background: '#f59e0b', color: '#000', padding: '5px 12px', borderRadius: '8px', fontSize: '11px', fontWeight: '900' }}>
                                        {pkg.badge.toUpperCase()}
                                    </div>
                                )}

                                <div style={{ marginBottom: '20px' }}>
                                    <h3 style={{ fontSize: '22px', fontWeight: '900', color: '#fff', marginBottom: '4px', textTransform: 'uppercase' }}>{pkg.name}</h3>
                                    {pkg.subtitle && <span style={{ fontSize: '12px', color: '#f59e0b', fontWeight: '800' }}>{pkg.subtitle}</span>}
                                </div>

                                <div style={{ minHeight: '44px', marginBottom: '20px' }}>
                                    <p style={{ fontSize: '14px', color: '#d1d5db', lineHeight: '1.5', fontWeight: '400' }}>{pkg.description}</p>
                                </div>

                                {/* Price */}
                                <div style={{ display: 'flex', alignItems: 'baseline', marginBottom: '24px', gap: '4px' }}>
                                    <span style={{ fontSize: '36px', fontWeight: '900', color: '#fff' }}>{priceData.price}</span>
                                    <span style={{ fontSize: '14px', color: '#9ca3af', fontWeight: '700' }}>{priceData.period}</span>
                                    {priceData.oldPrice && <s style={{ fontSize: '14px', color: '#4b5563', marginLeft: '6px', fontWeight: '600' }}>{priceData.oldPrice}</s>}
                                </div>

                                {/* CTA button */}
                                <button
                                    onClick={(e) => { e.stopPropagation(); handleCardClick(pkg); }}
                                    style={{
                                        width: '100%',
                                        padding: '14px',
                                        borderRadius: '12px',
                                        border: 'none',
                                        background: isFeatured ? '#f59e0b' : 'rgba(255,255,255,0.1)',
                                        color: isFeatured ? '#000' : '#fff',
                                        fontWeight: '900',
                                        fontSize: '14px',
                                        cursor: 'pointer',
                                        marginBottom: '24px',
                                        transition: 'all 0.2s',
                                        display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px',
                                        fontFamily: '"Outfit", sans-serif',
                                    }}
                                >
                                    {getCtaLabel(pkg)}
                                    {isFeatured && <Zap size={16} fill="currentColor" />}
                                </button>

                                {/* Quick feature list */}
                                <div style={{ flex: 1, borderTop: '1px solid rgba(255,255,255,0.1)', paddingTop: '20px' }}>
                                    {(pkg.features || []).slice(0, 6).map((feature, i) => (
                                        <div key={i} style={{ display: 'flex', alignItems: 'start', marginBottom: '10px', opacity: feature.included ? 1 : 0.3 }}>
                                            <div style={{ marginRight: '10px', marginTop: '3px' }}>
                                                {feature.included
                                                    ? <Lightbulb size={14} color={isFeatured ? '#f59e0b' : '#fff'} strokeWidth={3} />
                                                    : <LightbulbOff size={14} color="#4b5563" />
                                                }
                                            </div>
                                            <span style={{ fontSize: '13px', color: feature.included ? '#fff' : '#9ca3af', fontWeight: feature.included ? '600' : '400' }}>
                                                {feature.name}
                                            </span>
                                        </div>
                                    ))}
                                    {(pkg.features || []).length > 6 && (
                                        <div style={{ fontSize: '12px', color: '#f59e0b', fontWeight: 800, marginTop: '8px', cursor: 'pointer' }}
                                            onClick={(e) => { e.stopPropagation(); handleCardClick(pkg); }}>
                                            +{pkg.features.length - 6} more features →
                                        </div>
                                    )}
                                </div>
                            </motion.div>
                        );
                    })}
                </div>

                {/* ── Institutional Wide Card ────────────────── */}
                {!loading && institutionalPkg && (
                    <motion.div
                        whileHover={{ scale: 1.005 }}
                        style={{
                            background: 'rgba(99, 102, 241, 0.05)',
                            border: '1px solid rgba(99, 102, 241, 0.4)',
                            borderRadius: '24px',
                            padding: '30px 40px',
                            display: 'flex',
                            flexDirection: 'row',
                            alignItems: 'center',
                            gap: '40px',
                            position: 'relative',
                            marginTop: '20px'
                        }}
                    >
                        <div style={{ position: 'absolute', top: '-14px', right: '40px', background: '#6366f1', color: '#fff', padding: '5px 12px', borderRadius: '8px', fontSize: '11px', fontWeight: '900' }}>
                            ENTERPRISE
                        </div>

                        <div style={{ flex: 1 }}>
                            <h3 style={{ fontSize: '32px', fontWeight: '900', color: '#fff', marginBottom: '8px', textTransform: 'uppercase' }}>{institutionalPkg.name}</h3>
                            <p style={{ fontSize: '16px', color: '#a5b4fc', fontWeight: '600', marginBottom: '12px' }}>{institutionalPkg.subtitle}</p>
                            <p style={{ fontSize: '15px', color: '#d1d5db', lineHeight: '1.6' }}>{institutionalPkg.description}</p>

                            <div style={{ display: 'flex', alignItems: 'baseline', marginTop: '24px', gap: '8px' }}>
                                <span style={{ fontSize: '42px', fontWeight: '900', color: '#818cf8' }}>{getPriceData(institutionalPkg).price}</span>
                                <span style={{ fontSize: '18px', color: '#9ca3af', fontWeight: '700' }}>{getPriceData(institutionalPkg).period}</span>
                            </div>

                            <button
                                onClick={() => handleCardClick(institutionalPkg)}
                                style={{
                                    marginTop: '24px',
                                    width: '240px',
                                    padding: '16px',
                                    borderRadius: '14px',
                                    background: '#6366f1',
                                    color: '#fff',
                                    fontWeight: '900',
                                    fontSize: '16px',
                                    cursor: 'pointer',
                                    border: 'none',
                                    boxShadow: '0 10px 20px rgba(99, 102, 241, 0.2)',
                                    fontFamily: '"Outfit", sans-serif',
                                }}
                            >
                                CONTACT FOR DEMO
                            </button>
                        </div>

                        <div style={{ flex: 1.2, borderLeft: '1px solid rgba(99, 102, 241, 0.2)', paddingLeft: '40px', display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px' }}>
                            {(institutionalPkg.features || []).slice(0, 10).map((feature, i) => (
                                <div key={i} style={{ display: 'flex', alignItems: 'start', opacity: feature.included ? 1 : 0.3 }}>
                                    <div style={{ marginRight: '10px', marginTop: '3px' }}>
                                        <ShieldCheck size={18} color="#818cf8" strokeWidth={3} />
                                    </div>
                                    <span style={{ fontSize: '13px', color: '#fff', fontWeight: '600' }}>{feature.name}</span>
                                </div>
                            ))}
                        </div>
                    </motion.div>
                )}

                {/* ── Trust footer ───────────────────────────── */}
                <div style={{ textAlign: 'center', marginTop: '60px', padding: '30px', borderTop: '1px solid rgba(255,255,255,0.05)' }}>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '12px', color: '#6b7280', fontSize: '13px', fontWeight: '800', textTransform: 'uppercase', letterSpacing: '1px' }}>
                        <Lock size={16} /> <span>SECURE SSL ENCRYPTED TRANSACTION • GST COMPLIANT</span>
                    </div>
                </div>
            </div>

            {/* ── Plan Detail Modal ──────────────────────────── */}
            {selectedPlan && (
                <PlanDetailModal
                    pkg={selectedPlan}
                    billingCycle={billingCycle}
                    onClose={() => setSelectedPlan(null)}
                    onSubscribe={handleSubscribe}
                />
            )}

            {/* ── Billing Address Modal ──────────────────────── */}
            <BillingAddressModal
                isOpen={!!billingPlan}
                onClose={() => { setBillingPlan(null); setPayError(''); }}
                onConfirm={handleBillingConfirmed}
                planName={billingPlan?.name || ''}
                loading={payLoading}
            />

            {/* ── Payment error toast ───────────────────────── */}
            {payError && (
                <div style={{
                    position: 'fixed', bottom: '32px', left: '50%', transform: 'translateX(-50%)',
                    background: '#1e0a0a', border: '1px solid #dc2626', borderRadius: '12px',
                    padding: '14px 24px', color: '#fca5a5', fontSize: '14px', fontWeight: 700,
                    zIndex: 20000, maxWidth: '500px', textAlign: 'center',
                    boxShadow: '0 8px 32px rgba(220,38,38,0.3)',
                }}>
                    ⚠️ {payError}
                    <button
                        onClick={() => setPayError('')}
                        style={{ marginLeft: '16px', background: 'none', border: 'none', color: '#f87171', cursor: 'pointer', fontSize: '16px', fontWeight: 900 }}
                    >✕</button>
                </div>
            )}
        </div>
    );
};

export default PricingPage;
